import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'framer-motion'

import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import { Browser } from '@capacitor/browser'

import { useUserStore } from '@/store/useUserStore'

import type { DriveCopyStatus } from '@/types/expense'

import DriveConnectSheet from './DriveConnectSheet'
import DriveManagementSheet from './DriveManagementSheet'

export const DriveSheets = ({ copyState }: { copyState?: DriveCopyStatus | null }) => {
  const { t } = useTranslation()

  const sync = useUserStore((state) => state.sync)
  const [searchParams, setSearchParams] = useSearchParams()
  const [toast, setToast] = useState<'connected' | 'error' | null>(null)

  useEffect(() => {
    const result = searchParams.get('google_drive')
    if (result !== 'connected' && result !== 'error') return

    if (result === 'connected') sync()

    setToast(result)
    searchParams.delete('google_drive')
    setSearchParams(searchParams, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!toast) return

    const timer = setTimeout(() => setToast(null), 2500)
    return () => clearTimeout(timer)
  }, [toast])

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return

    const opened = App.addListener('appUrlOpen', ({ url }) => {
      const result = new URL(url).searchParams.get('google_drive')
      if (result !== 'connected' && result !== 'error') return

      Browser.close().catch(() => undefined)

      if (result === 'connected') sync()
      setToast(result)
    })

    const resumed = App.addListener('resume', () => { sync() })

    return () => {
      opened.then((handle) => handle.remove())
      resumed.then((handle) => handle.remove())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <DriveConnectSheet />
      <DriveManagementSheet copyState={copyState} />

      <AnimatePresence>
        {toast && (
          <motion.div
            key="drive-toast"
            className={`fixed top-[calc(var(--safe-area-inset-top,0px)_+_1.5rem)] left-1/2 z-50 text-white text-sm font-medium px-4 py-2.5 rounded-full shadow-lg whitespace-nowrap ${toast === 'connected' ? 'bg-zinc-900' : 'bg-amber-600'}`}
            initial={{ opacity: 0, y: -10, x: '-50%' }}
            animate={{ opacity: 1, y: 0, x: '-50%' }}
            exit={{ opacity: 0, y: -10, x: '-50%' }}
          >
            {toast === 'connected' ? t('expenses.drive.toast') : t('expenses.drive.connectSheet.error')}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default DriveSheets
